import React from "react";
import MobileThemeToggle from "./MobileThemeToggle";

/* ============================================================
   MobilePageHeader
   ----------------------------------------------------------------
   Single, consistent top bar used by every mobile screen so the
   user can always glance at the top and know which page they're
   on. Three-column layout:

     [ back? ]           [ title  ]            [ right? ]
                         [ sub?   ]

   - `title` is the main page label (e.g. "New Estimate",
     "Proposal", "Reset Password"). Rendered uppercase via CSS so
     callers pass title-case strings.
   - `subtitle` shows secondary context — used by the proposal
     screen to say which estimate's proposal it's reviewing
     ("for Mr. Smith — Commack").
   - `back` is an explicit labeled affordance ("‹ Estimator",
     "‹ Log in") — not just an arrow — so the user knows where
     the chevron will send them.
   - `right` is a slot for tail-side controls; defaults to the
     theme toggle unless `showThemeToggle` is false (the auth
     screens stay chromeless until the user signs in).

   Lives both inside .mobile-app (tab screens, proposal overlay)
   and standalone (.m-auth / reset-password — those don't render
   under .mobile-app), so the CSS is intentionally NOT scoped
   under .mobile-app.
   ============================================================ */

type BackProp = {
  /** Visible label next to the chevron — name of the destination
   *  ("Estimator", "Log in"). Avoid bare arrows. */
  label: string;
  onClick: () => void;
  ariaLabel?: string;
};

export type MobilePageHeaderProps = {
  title: string;
  subtitle?: string;
  back?: BackProp;
  right?: React.ReactNode;
  /** Lead-side action rendered in the left cluster (e.g. the Estimate tab's
   *  "New" button). Only used on screens without a `back` affordance, which
   *  owns the left slot otherwise. */
  leftAction?: React.ReactNode;
  /** Default true. Auth screens pass false to stay chromeless. */
  showThemeToggle?: boolean;
};

export default function MobilePageHeader({
  title,
  subtitle,
  back,
  right,
  leftAction,
  showThemeToggle = true,
}: MobilePageHeaderProps) {
  const rightNode = right ?? (showThemeToggle ? <MobileThemeToggle /> : null);
  return (
    <header
      className={`m-page-header${subtitle ? " has-sub" : ""}${
        back ? " has-back" : ""
      }`}
    >
      <div className="m-page-header-left">
        {back ? (
          <button
            type="button"
            className="m-page-back"
            onClick={back.onClick}
            aria-label={back.ariaLabel || `Back to ${back.label}`}
          >
            <svg viewBox="0 0 24 24" aria-hidden focusable="false">
              <polyline
                points="15 6 9 12 15 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="m-page-back-label">{back.label}</span>
          </button>
        ) : null}
        {!back && leftAction ? leftAction : null}
      </div>

      <div className="m-page-header-center">
        <div className="m-page-title">{title}</div>
        {subtitle ? <div className="m-page-subtitle">{subtitle}</div> : null}
      </div>

      <div className="m-page-header-right">{rightNode}</div>
    </header>
  );
}
